import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import HtmlWithVocab from "./HtmlWithVocab";
import { escapeHtml } from "../../utils/vocabHighlighter";

/**
 * 聽力逐字稿＋逐句重播。
 *
 * 刻意不重用 AudioPlay——那個元件綁著考試流程的語意（進第幾頁自動播、
 * 播了就不能停、離開偵測）。詳解頁要的是完全相反的東西：學生自己控制、
 * 想聽哪句點哪句。
 *
 * 逐句重播＝跳到該句 start_ms、放到 end_ms 自動停。沒有時間標記的
 * 句子只顯示文字（教務還沒標，或這句不需要）。句子文字也過單字
 * highlighter——逐字稿裡的字同樣可以點出補充。
 *
 * seek 包在 loadedmetadata 之後才做（還沒載入 metadata 就設
 * currentTime 會被瀏覽器丟掉）——與續考功能驗證過的寫法相同。
 */

const StyledTranscript = styled.div`
  text-align: left;

  .ce-tr-audio {
    position: sticky;
    top: 0;
    z-index: 5;
    background: #fff;
    padding: 8px 0;
    audio {
      width: 100%;
    }
  }

  .ce-tr-row {
    display: flex;
    gap: 10px;
    padding: 7px 10px;
    border-radius: 6px;
    align-items: flex-start;
  }
  .ce-tr-row:nth-child(odd) {
    background: #f7f8fa;
  }
  .ce-tr-row.is-playing {
    background: #e8f4ee;
  }

  .ce-tr-play {
    flex: 0 0 auto;
    border: 1px solid #cfd6dd;
    background: #fff;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    line-height: 1;
    cursor: pointer;
    font-size: 12px;
    color: #2f5d50;
  }
  .ce-tr-play:hover {
    background: #e8f4ee;
  }
  .ce-tr-play.is-stop {
    color: #a8432a;
  }
  .ce-tr-spacer {
    flex: 0 0 auto;
    width: 30px;
  }

  .ce-tr-text {
    font-size: 15px;
    line-height: 1.8;
    padding-top: 3px;
  }
`;

const TranscriptPlayer = ({ audio, sentences, vocabularies }) => {
  const playerRef = useRef(null);
  const stopAtRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const [playingIndex, setPlayingIndex] = useState(-1);

  // 離開詳解頁（unmount）一定要把聲音停掉——背景一直播、學生以為
  // 電腦壞了的事故發生過一次，不能再有第二次
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.pause();
      }
    };
  }, []);

  const playSentence = (index) => {
    const player = playerRef.current;
    const sentence = sentences[index];
    if (!player || !sentence || sentence.start_ms === null) {
      return;
    }

    // 再點一次正在播的那句＝停
    if (playingIndex === index) {
      player.pause();
      stopAtRef.current = null;
      setPlayingIndex(-1);
      return;
    }

    stopAtRef.current = sentence.end_ms;
    const seekTo = sentence.start_ms / 1000;

    if (player.readyState >= 1) {
      player.currentTime = seekTo;
      player.play();
    } else {
      // metadata 還沒好——記下來，loadedmetadata 再 seek
      pendingSeekRef.current = seekTo;
      player.play();
    }
    setPlayingIndex(index);
  };

  const handleLoadedMetadata = () => {
    const player = playerRef.current;
    if (player && pendingSeekRef.current !== null) {
      try {
        player.currentTime = pendingSeekRef.current;
      } catch (e) {
        // 老瀏覽器 seek 失敗就從頭放，不擋著不放
      }
      pendingSeekRef.current = null;
    }
  };

  const handleTimeUpdate = () => {
    const player = playerRef.current;
    if (!player || stopAtRef.current === null) {
      return;
    }
    if (player.currentTime * 1000 >= stopAtRef.current) {
      player.pause();
      stopAtRef.current = null;
      setPlayingIndex(-1);
    }
  };

  if (!sentences || sentences.length === 0) {
    return null;
  }

  return (
    <StyledTranscript>
      {audio && (
        <div className="ce-tr-audio">
          <audio
            ref={playerRef}
            controls
            src={audio}
            preload="metadata"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPause={() => {
              // 學生自己按了播放器的暫停，句子的高亮也要跟著收掉
              if (stopAtRef.current === null) {
                setPlayingIndex(-1);
              }
            }}
          >
            <track kind="captions" />
          </audio>
        </div>
      )}
      {sentences.map((s, i) => (
        <div key={s.id || i} className={`ce-tr-row${playingIndex === i ? " is-playing" : ""}`}>
          {audio && s.start_ms !== null ? (
            <button
              type="button"
              className={`ce-tr-play${playingIndex === i ? " is-stop" : ""}`}
              title={playingIndex === i ? "停止" : "播放這一句"}
              onClick={() => playSentence(i)}
            >
              {playingIndex === i ? "■" : "▶"}
            </button>
          ) : (
            <span className="ce-tr-spacer" />
          )}
          <div className="ce-tr-text">
            <HtmlWithVocab html={escapeHtml(s.text)} vocabularies={vocabularies} />
          </div>
        </div>
      ))}
    </StyledTranscript>
  );
};

export default TranscriptPlayer;
